var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import Cookies from 'js-cookie';
import Bowser from 'bowser';
import { getCookieDomain } from '@bombas/react/util';
import { isBrowser } from '../ssr';
import { parseCookie } from '../util';
var OPTIMIZELY_USER_ATTR_COOKIE = 'optimizely_user_attr';
/**
 * Get the device attributes for the user.
 */
export var getDeviceAttrs = function (userAgent) {
    var browser = Bowser.getParser(userAgent);
    return {
        Device: browser.getPlatformType() || '',
        Browser: browser.getBrowserName() || ''
    };
};
/**
 * Determines the marketing channel based on UTM parameters and referrer information.
 * @param utmMedium - The utm_medium URL parameter value
 * @param utmSource - The utm_source URL parameter value
 * @param referrer - The document referrer URL (optional, defaults to empty string)
 * @returns The marketing channel as a string
 */
export var determineMarketingChannel = function (utmMedium, utmSource, referrer) {
    var _a;
    if (referrer === void 0) { referrer = ''; }
    var normalizedReferrer = referrer.toLowerCase();
    // If no UTM source, determine channel based on referrer
    if (!utmSource) {
        var searchEngines = [
            'google',
            'duckduckgo',
            'search.yahoo',
            'bing',
            'msn',
            'search.aol'
        ];
        var socialPlatforms = ['instagram', 'facebook', 'pinterest', 'tiktok'];
        if (searchEngines.some(function (engine) { return normalizedReferrer.includes(engine); })) {
            return 'organic_search';
        }
        if (socialPlatforms.some(function (platform) { return normalizedReferrer.includes(platform); })) {
            return 'organic_social';
        }
        return 'direct';
    }
    // UTM source exists - define channel mappings
    var channelMap = {
        paid_search: function () {
            return ['google', 'bing'].includes(utmSource) &&
                ['cpc', 'shopping'].includes(utmMedium);
        },
        paid_social: function () {
            return ['facebook', 'Facebook', 'tiktok', 'pinterest'].includes(utmSource);
        },
        'email/text': function () { return ['attentive', 'braze', 'iterable'].includes(utmSource); },
        affiliate: function () {
            return ['pepperjam', 'morningbrew', 'theskimm'].includes(utmSource);
        },
        display: function () {
            return ['google', 'bing', 'taboola'].includes(utmSource) &&
                utmMedium === 'display';
        }
    };
    // Find matching channel
    return (((_a = Object.entries(channelMap).find(function (_a) {
        var condition = _a[1];
        return condition();
    })) === null || _a === void 0 ? void 0 : _a[0]) || null);
};
/**
 * Gets the users marketing channel
 * @param req (optional) - The API request object, used to extract UTM parameters from the URL server side.
 * @returns The marketing channel as a string or null if it cannot be determined.
 */
export var getMarketingChannelAttr = function (req) {
    // If we are on the server and req was passed - use it to determine the marketing channel
    if (req) {
        var url = req.url;
        // get the UTM medium and UTM source data from the URL
        var urlParams = url === null || url === void 0 ? void 0 : url.split('?')[1];
        var utmMediumParam = urlParams === null || urlParams === void 0 ? void 0 : urlParams.split('&').find(function (param) { return param.startsWith('utm_medium='); });
        var utmSourceParam = urlParams === null || urlParams === void 0 ? void 0 : urlParams.split('&').find(function (param) { return param.startsWith('utm_source='); });
        var utmMedium = utmMediumParam
            ? decodeURIComponent(utmMediumParam.split('=')[1])
            : '';
        var utmSource = utmSourceParam
            ? decodeURIComponent(utmSourceParam.split('=')[1])
            : '';
        return determineMarketingChannel(utmMedium, utmSource, req.headers.referer || '');
    }
    // If we are on the client, use the URL to determine the marketing channel
    if (isBrowser()) {
        var urlParams = new URLSearchParams(window.location.search);
        var utmMedium = urlParams.get('utm_medium') || '';
        var utmSource = urlParams.get('utm_source') || '';
        return determineMarketingChannel(utmMedium, utmSource, document.referrer);
    }
    // If we cannot determine the marketing channel, return null
    return null;
};
/**
 * Helper function that gets a user’s user-level attributes.
 * It uses a session cookie on the client to ensure attributes are consistent.
 */
export var getUserAttributes = function (req) {
    var _a;
    if (!isBrowser() && req) {
        var userAgent = req.headers['user-agent'] || '';
        var userAttributesCookie = parseCookie(OPTIMIZELY_USER_ATTR_COOKIE, req);
        var marketingChannel = (userAttributesCookie === null || userAttributesCookie === void 0 ? void 0 : userAttributesCookie.MarketingChannel) || getMarketingChannelAttr(req);
        var newOrReturning = (userAttributesCookie === null || userAttributesCookie === void 0 ? void 0 : userAttributesCookie.New_Or_Returning) ||
            (((_a = req.cookies) === null || _a === void 0 ? void 0 : _a.optimizelyNewCustomerOfferEmailCaptureImpression)
                ? 'returning'
                : 'new');
        return __assign(__assign({}, getDeviceAttrs(userAgent)), { 
            // This applies Optimizely's bot filtering
            $opt_user_agent: userAgent, New_Or_Returning: newOrReturning, MarketingChannel: marketingChannel });
    }
    if (isBrowser()) {
        var existingAttributes = parseCookie(OPTIMIZELY_USER_ATTR_COOKIE);
        if (existingAttributes) {
            return existingAttributes;
        }
        var attributes = __assign(__assign({}, getDeviceAttrs(window.navigator.userAgent)), { New_Or_Returning: sessionStorage.getItem('_bom_new_visitor') ||
                !Cookies.get('optimizelyNewCustomerOfferEmailCaptureImpression')
                ? 'new'
                : 'returning', MarketingChannel: getMarketingChannelAttr() });
        Cookies.set(OPTIMIZELY_USER_ATTR_COOKIE, JSON.stringify(attributes), {
            domain: getCookieDomain(),
            sameSite: 'Lax'
        });
        return attributes;
    }
    return {};
};
